import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { type InputType } from 'config'
import type { FormTableHeader } from 'stories/components'

export const header: FormTableHeader[] = [
  { key: 'accountType', title: 'Account Type' },
  { key: 'institution', title: 'Institution' },
  { key: 'accountNumber', title: 'Account Number' },
  { key: 'balance', title: 'Balance' },
  { key: 'verified', title: 'Verified' },
]

export const defaultInputs = (type: 'asset' | 'otherAsset' = 'asset'): Record<string, InputType> => {
  return {
    section1: {
      inputType: 'section',
      title: 'Account Information',
      span: 'full',
    },
    accountType: {
      inputType: 'select',
      options: type === 'asset' ? AssetAccountTypeOptions : OtherAssetAccountTypeOptions,
      hasDefaultOption: true,
      title: 'Account Type',
      sort: true,
      required: true,
    },
    institution: {
      inputType: 'text',
      title: 'Institution',
      required: true,
    },
    accountNumber: {
      inputType: 'text',
      title: 'Account Number',
      required: false,
    },
    balance: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Account Balance',
      required: true,
      prefix: '$',
    },
    override: {
      inputType: 'toggle',
      title: 'Override Validation',
    },

    section2: {
      inputType: 'section',
      title: 'Contact Information',
      span: 'full',
    },
    attn: {
      inputType: 'text',
      title: 'Attn',
      required: false,
    },
    address: {
      inputType: 'map',
      title: 'Address',
      required: false,
      allowForeign: true,
      sitexPro: false,
    },
    email: {
      inputType: 'text',
      type: 'email',
      title: 'Email',
      required: false,
    },
    phone: {
      inputType: 'text',
      type: 'phone',
      title: 'Phone',
      required: false,
    },
    fax: {
      inputType: 'text',
      title: 'Fax',
      required: false,
    },
    notes: {
      inputType: 'textarea',
      title: 'Notes',
      required: false,
      span: 'full',
      rows: 3,
    },
  }
}

export const requiredInputs = ['balance']

export interface IAssetData {
  id: string
  accountType?: number
  institution: string
  accountNumber: string
  balance: number

  mask: string
  name: string
  verified: boolean
  accessToken: string

  // Plaid
  assetReportId: string
  assetReportToken: string
  assetReportFileKey: string

  // Snappt Bank Statement
  bankStatementFileKey: string
  bankStatementData: {
    appSession: Record<string, any>
    applicantId: string | null
    document: Record<string, any> | null
    webhookDocumentData: Record<string, any> | null
    webhookReportData: Record<string, any> | null
  }
  bankStatementReportFileKey: string

  override: boolean

  createdAt: number
  creatorEmail: string

  attn?: string
  address?: string
  email?: string
  phone?: string
  fax?: string
  notes?: string
}

export const AssetAccountTypeForPlaid = [5, 2, 1, 6]

export const AssetAccountTypeOptions: Record<number, string> = {
  9: 'Bonds',
  11: 'Bridge Loan Not Deposited',
  5: 'Certificate of Deposit', // Plaid
  2: 'Checking', // Plaid
  21: 'Individual Development Account',
  22: 'Life Insurance Cash Value',
  6: 'Money Market Fund', // Plaid
  7: 'Mutual Funds',
  12: 'Retirement Funds',
  1: 'Savings', // Plaid
  25: 'Stock Options',
  8: 'Stocks',
  14: 'Trust Funds',
  20: 'Gift',
  23: 'Proceeds from Sale/Refi',
}

export const OtherAssetAccountTypeOptions: Record<number, string> = {
  19: 'Cash on Hand',
  17: 'Net Proceeds from Sale of Real Estate',
  16: 'Other Liquid Asset',
  15: 'Other Non Liquid Asset',
  23: 'Proceeds from Sale of Non Real Estate Asset',
  24: 'Secured Borrowed Funds',
  26: 'Unsecured Borrowed Funds',
  20: 'Gift of Equity',
}

export const AssetAccountTypeOptionsRev: Record<string, string> = Object.fromEntries(
  Object.entries(AssetAccountTypeOptions).map(([key, value]) => [value, key]),
)

export const renderAccountName = (asset: IAssetData) => {
  return asset.name ? `${asset.name} (···· ${asset.mask})` : asset.accountNumber || ''
}

export const emailDefaultInputs = (): Record<string, InputType> => {
  return {
    email: {
      inputType: 'text',
      type: 'email',
      title: 'Email',
      required: true,
    },
  }
}

const snapptVideoUrl = 'https://vimeo.com/645372425'

export const SnapptVideoLink = (
  <a className="btn-link flex gap-2 items-center !text-sm mt-4" href={snapptVideoUrl} target="_blank">
    How to properly download documents?
    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
  </a>
)

export const PurchaseCreditTypes: Record<string, string> = {
  employerAssistance: 'Employer Assistance',
  rentCredit: 'Rental Credit',
  rebate: 'Rebate',
  relocationFunds: 'Relocation Funds',
  sweatEquity: 'Sweat Equity',
  tradeEquity: 'Trade Equity',
  lotEquity: 'Lot Equity',
  closingCosts: 'Closing Costs',
}

export const PurchaseCreditSources: Record<string, string> = {
  0: 'Not Assigned',
  1: 'Borrower Paid Outside of Closing',
  2: 'Builder/Developer',
  3: 'Employer',
  4: 'Federal Agency',
  5: 'Lender',
  6: 'Local Agency',
  7: 'Non Parent Relative',
  8: 'Other',
  9: 'Parent',
  10: 'Property Seller',
  11: 'Real Estate Agent',
  12: 'State Agency',
  13: 'Unrelated Friend',
  14: 'Employer Affiliate',
  15: 'Lender Affiliate',
  16: 'Other Interested Party',
}
